import React from "react";
import { Search, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// 50 covered jurisdictions (48 states + DC), grouped by U.S. Census region.
// 2026-09 expansion added: CT, DE, MA, MD, MT, NH, NJ, OH, OR, PA, UT.
export const JURIS_REGIONS = [
  {
    region: "Northeast",
    states: [
      { abbr: "CT", name: "Connecticut" },
      { abbr: "MA", name: "Massachusetts" },
      { abbr: "ME", name: "Maine" },
      { abbr: "NH", name: "New Hampshire" },
      { abbr: "NJ", name: "New Jersey" },
      { abbr: "NY", name: "New York" },
      { abbr: "PA", name: "Pennsylvania" },
      { abbr: "VT", name: "Vermont" },
    ],
  },
  {
    region: "South",
    states: [
      { abbr: "AL", name: "Alabama" },
      { abbr: "AR", name: "Arkansas" },
      { abbr: "DC", name: "Washington D.C." },
      { abbr: "DE", name: "Delaware" },
      { abbr: "FL", name: "Florida" },
      { abbr: "GA", name: "Georgia" },
      { abbr: "KY", name: "Kentucky" },
      { abbr: "LA", name: "Louisiana" },
      { abbr: "MD", name: "Maryland" },
      { abbr: "MS", name: "Mississippi" },
      { abbr: "NC", name: "North Carolina" },
      { abbr: "OK", name: "Oklahoma" },
      { abbr: "SC", name: "South Carolina" },
      { abbr: "TN", name: "Tennessee" },
      { abbr: "TX", name: "Texas" },
      { abbr: "VA", name: "Virginia" },
      { abbr: "WV", name: "West Virginia" },
    ],
  },
  {
    region: "Midwest",
    states: [
      { abbr: "IA", name: "Iowa" },
      { abbr: "IL", name: "Illinois" },
      { abbr: "IN", name: "Indiana" },
      { abbr: "KS", name: "Kansas" },
      { abbr: "MI", name: "Michigan" },
      { abbr: "MN", name: "Minnesota" },
      { abbr: "MO", name: "Missouri" },
      { abbr: "ND", name: "North Dakota" },
      { abbr: "NE", name: "Nebraska" },
      { abbr: "OH", name: "Ohio" },
      { abbr: "SD", name: "South Dakota" },
      { abbr: "WI", name: "Wisconsin" },
    ],
  },
  {
    region: "West",
    states: [
      { abbr: "AK", name: "Alaska" },
      { abbr: "AZ", name: "Arizona" },
      { abbr: "CA", name: "California" },
      { abbr: "CO", name: "Colorado" },
      { abbr: "HI", name: "Hawaii" },
      { abbr: "ID", name: "Idaho" },
      { abbr: "MT", name: "Montana" },
      { abbr: "NM", name: "New Mexico" },
      { abbr: "NV", name: "Nevada" },
      { abbr: "OR", name: "Oregon" },
      { abbr: "UT", name: "Utah" },
      { abbr: "WA", name: "Washington" },
      { abbr: "WY", name: "Wyoming" },
    ],
  },
];

export const JURIS = JURIS_REGIONS.flatMap((r) => r.states);
export const TOTAL_JURIS_COUNT = JURIS.length; // 50

/**
 * Stateful UI for choosing from the 50 covered jurisdictions.
 * Renders region accordions, a filter, a counter, and clear-all.
 * Parent owns the `selected` state.
 */
export function JurisdictionSelector({ selected, onToggle, onClear, filter, onFilterChange }) {
  const q = filter.trim().toLowerCase();
  return (
    <div>
      <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
        Which jurisdictions do you ship or sell to — even in small or occasional amounts?
      </Label>
      <p className="text-xs text-[var(--rg-muted)] mt-1 mb-3">
        Coverage: 50 tax jurisdictions (48 states + DC). Include any jurisdiction where you ship even one order. This is critical for New York&apos;s and Connecticut&apos;s dual-threshold (AND-gate) calculations.
      </p>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--rg-muted)]" />
        <Input
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Filter by name or abbreviation (e.g. CA, Texas)"
          className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] pl-9 h-9 text-sm"
          data-testid="juris-filter-input"
        />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)]"
          data-testid="juris-selected-count"
        >
          Selected: <span className="text-[var(--rg-sage)]">{selected.length}</span> / {TOTAL_JURIS_COUNT}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] hover:text-[var(--rg-text-strong)] transition-fast"
            data-testid="juris-clear-all"
          >
            Clear all
          </button>
        )}
      </div>

      <Accordion
        type="multiple"
        defaultValue={["Northeast", "South", "Midwest", "West"]}
        className="border border-[var(--rg-border)] rounded-md bg-[var(--rg-surface)]"
        data-testid="juris-accordion"
      >
        {JURIS_REGIONS.map(({ region, states }) => {
          const visible = q
            ? states.filter(
                (j) =>
                  j.name.toLowerCase().includes(q) ||
                  j.abbr.toLowerCase().includes(q),
              )
            : states;
          if (q && visible.length === 0) return null;
          const regionSelectedCount = states.filter((j) => selected.includes(j.abbr)).length;
          return (
            <AccordionItem key={region} value={region} className="border-[var(--rg-border)]">
              <AccordionTrigger
                className="px-4 py-3 hover:no-underline hover:bg-[var(--rg-surface)] text-[var(--rg-text-strong)]"
                data-testid={`juris-region-${region.toLowerCase()}`}
              >
                <div className="flex items-center justify-between w-full pr-2">
                  <span className="font-heading font-semibold text-sm">{region}</span>
                  <span className="text-[10px] font-mono uppercase text-[var(--rg-muted)]">
                    {regionSelectedCount} / {states.length} selected
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {visible.map((j) => (
                    <label
                      key={j.abbr}
                      className={`flex items-center gap-2 rounded-md border p-2 cursor-pointer transition-fast ${
                        selected.includes(j.abbr)
                          ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                          : "border-[var(--rg-border)] hover:border-[var(--rg-border-hi)] bg-[var(--rg-bg)]"
                      }`}
                      data-testid={`onboarding-juris-${j.abbr}`}
                    >
                      <Checkbox
                        checked={selected.includes(j.abbr)}
                        onCheckedChange={() => onToggle(j.abbr)}
                        className="border-[var(--rg-border-hi)]"
                      />
                      <div className="min-w-0">
                        <div className="text-[var(--rg-text-strong)] text-xs font-semibold truncate">{j.name}</div>
                        <div className="text-[9px] font-mono text-[var(--rg-muted)] uppercase">{j.abbr}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {selected.includes("NY") && (
        <div
          className="mt-3 rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage)]/10 p-3 text-sm text-[var(--rg-text)]"
          data-testid="ny-callout"
        >
          <Info className="w-4 h-4 inline mr-1.5 text-[var(--rg-sage)]" />
          NY nexus requires BOTH $500K revenue AND 100+ transactions. Enter both below.
        </div>
      )}
    </div>
  );
}

/**
 * Per-state sales-tax registration checklist for the merchant's selected states.
 */
export function NexusRegistrationSection({ selected, regStates, onChange }) {
  if (selected.length === 0) return null;
  return (
    <div data-testid="nexus-registration-section">
      <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
        Sales-tax registration status per jurisdiction
      </Label>
      <p className="text-xs text-[var(--rg-muted)] mt-1 mb-3">
        Check each state where you are <strong className="text-[var(--rg-text-strong)]">currently registered</strong> to collect sales tax. Unchecked = not registered.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {selected.map((abbr) => {
          const stateMeta = JURIS.find((j) => j.abbr === abbr);
          const checked = !!regStates[abbr];
          return (
            <label
              key={abbr}
              className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-fast ${
                checked ? "border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]" : "border-[var(--rg-border)] hover:border-[var(--rg-border-hi)] bg-[var(--rg-surface)]"
              }`}
              data-testid={`nexus-reg-${abbr}`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={(v) => onChange(abbr, !!v)}
                className="border-[var(--rg-border-hi)]"
              />
              <div>
                <div className="text-[var(--rg-text-strong)] font-semibold text-sm">{stateMeta?.name || abbr}</div>
                <div className="text-[10px] font-mono text-[var(--rg-muted)] uppercase">
                  {checked ? "Registered" : "Not registered"}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
